<template>
  <div class="cadenceCol">
    <span class="cadenceLabel">
      <template v-if="state === 'timed_followup' && followupDate">
        On <span class="cadenceFollowupDate">{{ formatFollowupDate(followupDate) }}</span>
      </template>
      <template v-else>{{ cadenceLabel }}</template>
    </span>
    <button class="cadenceEditBtn" type="button" title="Change cadence" @click.stop="modalOpen = true">
      <AppIcon name="pencil" :size="12" />
    </button>
  </div>

  <Teleport to="body">
    <SetCadenceModal
      v-if="modalOpen"
      :count="1"
      :busy="false"
      :initial-state="state"
      :initial-date="followupDate"
      :active-days="activeDays"
      :opportunistic-days="opportunisticDays"
      @close="modalOpen = false"
      @confirm="onConfirm"
    />
  </Teleport>
</template>

<script lang="ts">
export const CADENCE_LABELS: Record<string, string> = {
  active: 'Active',
  opportunistic: 'Opportunistic',
  timed_followup: 'Follow-up',
  off: 'Off',
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'
import AppIcon from '../../components/AppIcon.vue'
import SetCadenceModal from './SetCadenceModal.vue'

const props = withDefaults(
  defineProps<{
    state: string
    followupDate: string | null
    /** The play's configured cadence, so the label reads "Every 1d" instead of
     *  a bucket name that hides what the number actually is. */
    activeDays?: number
    opportunisticDays?: number
  }>(),
  { activeDays: 1, opportunisticDays: 7 },
)

const cadenceLabel = computed(() => {
  if (props.state === 'active') return `Every ${props.activeDays}d`
  if (props.state === 'opportunistic') return `Every ${props.opportunisticDays}d`
  return CADENCE_LABELS[props.state] || props.state
})

const emit = defineEmits<{
  (e: 'update', state: string, followupDate: string | null): void
}>()

const modalOpen = ref(false)

function onConfirm(state: string, date: string | null) {
  modalOpen.value = false
  emit('update', state, date)
}

function formatFollowupDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
</script>

<style scoped>
.cadenceCol {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.cadenceLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cadenceFollowupDate {
  color: var(--color-text-tertiary);
  font-size: var(--fs-xs);
}
.cadenceEditBtn {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--color-text-secondary);
  opacity: 0;
  transition: opacity 0.1s;
}
.cadenceCol:hover .cadenceEditBtn { opacity: 0.6; }
.cadenceEditBtn:hover { opacity: 1 !important; }
</style>
